import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tray, PaperPlaneRight, Robot } from "@phosphor-icons/react";

const CATS = [
  ["all", "Toate"], ["reclamatie", "Reclamații"], ["oferta", "Oferte"],
  ["comanda", "Comenzi"], ["factura", "Facturi"], ["tehnic", "Tehnic"], ["logistica", "Logistică"],
];

const RECLASS = [
  ["oferta", "Ofertă"], ["comanda", "Comandă"], ["factura", "Factură"],
  ["reclamatie", "Reclamație"], ["tehnic", "Tehnic"], ["logistica", "Logistică"], ["altul", "Altul"],
];

export default function SmartInbox({ department = null, showAssistant = true }) {
  const [items, setItems] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [days, setDays] = useState(7);

  useEffect(() => {
    setItems(null);
    api.get(`/inbox/emails?days=${days}${department ? `&dept=${department}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    // eslint-disable-next-line
  }, [days, department]);

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    const history = msgs.slice(-6);
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setQ(""); setAsking(true);
    try {
      const { data } = await api.post("/ask-global", { question, history });
      setMsgs((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch { setMsgs((m) => [...m, { role: "assistant", content: "Eroare la răspuns." }]); }
    finally { setAsking(false); }
  };

  const filt = (c) => (c === "all" ? (items || []) : (items || []).filter((i) => i.category === c));
  const unread = (items || []).filter((i) => !i.opened).length;

  const markRead = async (id) => {
    setItems((arr) => (arr || []).map((i) => (i.id === id ? { ...i, opened: true } : i)));
    try { await api.post(`/inbox/emails/${id}/read`); window.dispatchEvent(new Event("inbox-read")); } catch { /* ignore */ }
  };

  const reclassify = async (id, cat) => {
    try {
      await api.put(`/inbox/emails/${id}/category`, { category: cat, notify: true });
      setItems((arr) => (arr || []).map((i) => (i.id === id ? { ...i, category: cat, category_label: RECLASS.find(([k]) => k === cat)?.[1] || "Email" } : i)));
      toast.success("Categorie corectată");
    } catch { toast.error("Nu s-a putut schimba categoria"); }
  };

  const withAssistant = showAssistant && !department;
  return (
    <div className={withAssistant ? "mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3" : "mt-6"}>
      <div className={`rounded-xl border border-border bg-card p-6 ${withAssistant ? "lg:col-span-2" : ""}`} data-testid="smart-inbox">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><Tray size={20} weight="bold" className="text-primary" /><h3 className="font-head text-lg font-bold">{department ? "Inbox — departament" : "Inbox inteligent"}</h3>{unread > 0 && <span className="rounded-full bg-red-500/90 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="inbox-unread-badge">{unread} noi</span>}</div>
          <div className="flex gap-1">
            {[["Azi", 1], ["7 zile", 7], ["Toate", 0]].map(([l, d]) => (
              <button key={d} onClick={() => setDays(d)} className={`rounded-full px-2.5 py-1 text-xs ${days === d ? "bg-primary/20 text-primary" : "text-muted-foreground hover:bg-white/5"}`} data-testid={`inbox-days-${d}`}>{l}</button>
            ))}
          </div>
        </div>
        {items === null ? <p className="text-sm text-muted-foreground">Se încarcă…</p> : (
          <Tabs defaultValue="all">
            <TabsList className="flex flex-wrap">
              {CATS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`inbox-tab-${k}`}>{l} ({filt(k).length})</TabsTrigger>)}
            </TabsList>
            {CATS.map(([k]) => (
              <TabsContent key={k} value={k} className="mt-3 max-h-96 space-y-2 overflow-y-auto">
                {filt(k).length === 0 && <p className="text-sm text-muted-foreground">Niciun email în această categorie.</p>}
                {filt(k).map((i) => (
                  <div key={i.id} className={`rounded-lg border px-3 py-2.5 transition-colors hover:bg-white/5 ${i.opened ? "border-border" : "border-primary/40 bg-primary/[0.04]"}`} data-testid={`inbox-item-${i.id}`}>
                    <Link to={`/projects/${i.project_id}?tab=dosar`} onClick={() => !i.opened && markRead(i.id)} className="block">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5">
                          {!i.opened && <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" data-testid={`inbox-unread-dot-${i.id}`} />}
                          <span className={`truncate text-sm ${i.opened ? "font-medium" : "font-bold"}`}>{i.subject}</span>
                        </span>
                        <span className="shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">{i.category_label}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground">{i.project_code || "—"} · {i.department} · {fmtDate(i.created_at)}</div>
                      {i.summary && <div className="mt-1 text-[11px] italic text-muted-foreground/90">🧠 {i.summary}</div>}
                    </Link>
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Corectează categoria:</span>
                      <Select value={i.category} onValueChange={(v) => reclassify(i.id, v)}>
                        <SelectTrigger className="h-7 w-36 text-xs" data-testid={`inbox-reclass-${i.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{RECLASS.map(([kk, ll]) => <SelectItem key={kk} value={kk}>{ll}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      {withAssistant && (
      <div className="rounded-xl border border-border bg-card p-6" data-testid="global-assistant">
        <div className="mb-3 flex items-center gap-2"><Robot size={20} weight="bold" className="text-primary" /><h3 className="font-head text-lg font-bold">Asistent global</h3></div>
        <p className="mb-3 text-xs text-muted-foreground">Caută în toate proiectele. Ex: „care proiecte au reclamații?", „unde sunt întârzieri?"</p>
        <div className="mb-3 max-h-72 space-y-2 overflow-y-auto">
          {msgs.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : "text-left"} data-testid={`global-msg-${i}`}>
              <span className={`inline-block max-w-[90%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-primary/20 text-foreground" : "bg-white/5 text-foreground/90"}`}>{m.content}</span>
            </div>
          ))}
          {asking && <div className="text-xs text-muted-foreground">Caut în proiecte…</div>}
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Întreabă despre toate proiectele…" data-testid="global-input" />
          <Button onClick={ask} disabled={asking || !q.trim()} data-testid="global-send"><PaperPlaneRight size={16} /></Button>
        </div>
      </div>
      )}
    </div>
  );
}
